/**
 * The lead gate's rules, as pure functions.
 *
 * Validation lives here rather than in the form so the messages the visitor
 * sees and the ones the tests assert are the same strings, and so the free
 * email check stays a soft nudge instead of drifting into a blocker.
 */

import type { ReportRequest } from "@/lib/api";

import {
  GATE_COMPANY_REQUIRED,
  GATE_EMAIL_INVALID,
  GATE_EMAIL_REQUIRED,
  GATE_FIRST_NAME_REQUIRED,
  GATE_LAST_NAME_REQUIRED,
  GATE_ROLE_REQUIRED,
} from "./copy";

/** The role values the backend accepts, in the order the select offers them. */
export const ROLE_OPTIONS: ReadonlyArray<ReportRequest["role"]> = [
  "C-suite",
  "VP or director",
  "Head of AI or data",
  "Transformation lead",
  "Other",
];

/** The form as the visitor has filled it in. `role` is empty until chosen. */
export interface GateValues {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  role: ReportRequest["role"] | "";
}

/** The fields that can carry a validation message. */
export type GateField = "firstName" | "lastName" | "email" | "company" | "role";

export type GateErrors = Partial<Record<GateField, string>>;

/**
 * Tab order. The first invalid field in this order takes focus on a failed
 * submit, so the visitor is sent to the topmost problem, not the last one.
 */
export const GATE_FIELDS: readonly GateField[] = [
  "firstName",
  "lastName",
  "email",
  "company",
  "role",
];

export const EMPTY_GATE_VALUES: GateValues = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  role: "",
};

/** Deliberately loose: the backend and the mail itself are the real check. */
const EMAIL_SHAPE = /.+@.+\..+/;

/** Consumer mailbox providers, matched on the whole domain. */
const FREE_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "mail.com",
]);

/**
 * Consumer providers with a country domain per market ("yahoo.co.uk"), matched
 * on the first label so every suffix is covered.
 */
const FREE_DOMAIN_ROOTS = ["yahoo", "hotmail", "outlook", "live", "gmx", "yandex"];

function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at === -1
    ? ""
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * Whether the address is on a consumer mailbox provider.
 *
 * This never blocks a submission: plenty of real buyers hand over a personal
 * address, and refusing them loses the lead. It only earns a soft note.
 */
export function isFreeEmailDomain(email: string): boolean {
  const domain = domainOf(email.trim());
  if (!domain) {
    return false;
  }
  if (FREE_DOMAINS.has(domain)) {
    return true;
  }
  const root = domain.split(".")[0] ?? "";
  return FREE_DOMAIN_ROOTS.includes(root);
}

/** Validate the gate. An empty object means the form can be submitted. */
export function validateGate(values: GateValues): GateErrors {
  const errors: GateErrors = {};

  if (!values.firstName.trim()) {
    errors.firstName = GATE_FIRST_NAME_REQUIRED;
  }

  if (!values.lastName.trim()) {
    errors.lastName = GATE_LAST_NAME_REQUIRED;
  }

  const email = values.email.trim();
  if (!email) {
    errors.email = GATE_EMAIL_REQUIRED;
  } else if (!EMAIL_SHAPE.test(email)) {
    errors.email = GATE_EMAIL_INVALID;
  }

  if (!values.company.trim()) {
    errors.company = GATE_COMPANY_REQUIRED;
  }

  if (!values.role) {
    errors.role = GATE_ROLE_REQUIRED;
  }

  return errors;
}

/** The first field, in tab order, that failed validation. */
export function firstInvalidField(errors: GateErrors): GateField | undefined {
  return GATE_FIELDS.find((field) => errors[field] !== undefined);
}

/**
 * The HTTP status behind a rejected SDK call.
 *
 * `src/lib/api/client.ts` wraps every failure as `{ status, body }`, with
 * status 0 for a transport error, so branching on 429 only needs the number.
 */
export function errorStatus(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null && "status" in error) {
    const { status } = error as { status: unknown };
    if (typeof status === "number") {
      return status;
    }
  }
  return undefined;
}
